import React, { useState, useRef } from 'react';
import { Field } from 'react-final-form';
import classNames from 'classnames';
import { useIntl } from '../../../../util/reactIntl';

import { OutsideClickHandler } from '../../../../components';

import css from './FilterCategories/FilterCategories.module.css';

// Grid icon matching the visual style of IconLocation / IconDate
const IconCategory = ({ style }) => (
  <svg
    style={style}
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="3" width="7" height="7" />
    <rect x="14" y="3" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" />
    <rect x="14" y="14" width="7" height="7" />
  </svg>
);

const CategoryDropdown = ({ input, className, rootClassName, options, alignLeft }) => {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [hasSelected, setHasSelected] = useState(false);
  const optionRefs = useRef([]);

  const toggleDropdown = () => {
    setIsOpen(prev => !prev);
    if (!isOpen) {
      setActiveIndex(-1);
    }
  };

  const handleOptionClick = optionId => {
    const value = optionId === 'all-categories' ? '' : `has_all:${optionId}`;
    input.onChange(value);
    setHasSelected(true);
    setIsOpen(false);
  };

  const handleKeyDown = e => {
    const allOptions = buildAllOptions();
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        e.preventDefault();
        setIsOpen(true);
        setActiveIndex(0);
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(prev => (prev < allOptions.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(prev => (prev > 0 ? prev - 1 : prev));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const selected = allOptions[activeIndex];
      if (selected) handleOptionClick(selected.option);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
    }
  };

  // Translate option key using existing i18n keys (fr/nl/en already defined)
  const translateOption = optionKey =>
    intl.formatMessage({
      id: `CustomExtendedDataField.CateoryFields.option.${optionKey}`,
      defaultMessage: optionKey,
    });

  const buildAllOptions = () => [{ option: 'all-categories' }, ...options];

  const selectedCategory = options.find(cat => `has_all:${cat.option}` === input.value);

  const labelText = selectedCategory
    ? translateOption(selectedCategory.option)
    : hasSelected && input.value === ''
    ? intl.formatMessage({ id: 'PageBuilder.SearchCTA.CategoryFilter.selectAll' })
    : intl.formatMessage({
        id: 'CustomExtendedDataField.CateoryFields.label',
        defaultMessage: 'Category',
      });

  const rootClass = rootClassName || css.root;
  const classes = classNames(rootClass, className);
  const allOptions = buildAllOptions();

  return (
    <OutsideClickHandler className={classes} onOutsideClick={() => setIsOpen(false)}>
      <div className={css.dropdownContainer}>
        <div
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-owns="cateory-fields-listbox"
          tabIndex={0}
          onClick={toggleDropdown}
          onKeyDown={handleKeyDown}
          className={classNames(css.toggleButton, {
            [css.unselected]: !hasSelected && !selectedCategory && !isOpen,
            [css.placeholderOpened]: isOpen && !hasSelected,
          })}
          style={{ display: 'flex', alignItems: 'center', gap: '6px', paddingLeft: '16px' }}
        >
          <IconCategory style={{ flexShrink: 0, color: 'var(--colorGrey600)', marginTop: '-2px' }} />
          <span className={css.dropdownItem}>{labelText}</span>
          <span className={classNames(css.chevron, isOpen && css.isOpen)} />
        </div>

        {isOpen && (
          <ul
            className={classNames(css.dropdownContent, { [css.alignLeft]: alignLeft })}
            role="listbox"
            id="cateory-fields-listbox"
          >
            {allOptions.map(({ option }, index) => {
              const value = option === 'all-categories' ? '' : `has_all:${option}`;
              const isSelected = value === input.value;
              const isActive = index === activeIndex;
              const label =
                option === 'all-categories'
                  ? intl.formatMessage({ id: 'PageBuilder.SearchCTA.CategoryFilter.selectAll' })
                  : translateOption(option);
              return (
                <li
                  key={option}
                  ref={opt => (optionRefs.current[index] = opt)}
                  className={classNames(css.option, { [css.activeOption]: isActive })}
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => handleOptionClick(option)}
                >
                  <span
                    className={isSelected ? css.dropdownItemBorderSelected : css.dropdownItemBorder}
                  />
                  {label}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </OutsideClickHandler>
  );
};

const FilterCateoryFields = props => {
  const { className, rootClassName, options, ...rest } = props;
  return (
    <Field
      {...rest}
      name="pub_CateoryFields"
      component={CategoryDropdown}
      className={className}
      rootClassName={rootClassName}
      options={options}
    />
  );
};

export default FilterCateoryFields;
